import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type {
  ClauseTreeNode,
  StandardVersion,
  StandardVersionDetail,
  VersionDiff,
} from '@grc/types';
import { apiFetch, apiUrl } from '../../lib/api.js';
import { useOrg } from '../../lib/auth.js';
import {
  Badge,
  Button,
  Card,
  Input,
  LoadingPane,
  PageHeader,
  Select,
  Table,
  formatDate,
} from '../../components/ui.js';

const CHANGE_TONES = { new: 'success', changed: 'warning', removed: 'danger', unchanged: 'neutral' } as const;

function matches(node: ClauseTreeNode, needle: string): boolean {
  const haystack =
    `${node.clauseNumber} ${node.title} ${node.questions.map((q) => q.text).join(' ')}`.toLowerCase();
  return haystack.includes(needle) || node.children.some((c) => matches(c, needle));
}

function ClauseNode({ node, search }: { node: ClauseTreeNode; search: string }) {
  const needle = search.trim().toLowerCase();
  const visible = !needle || matches(node, needle);
  const [open, setOpen] = useState(false);
  const expanded = open || (!!needle && visible);
  if (!visible) return null;

  return (
    <li style={{ marginBottom: 4 }}>
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={expanded}
        style={{
          font: 'inherit',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          gap: 8,
          alignItems: 'baseline',
          textAlign: 'left',
          padding: '4px 0',
        }}
      >
        <span aria-hidden="true">{expanded ? '▾' : '▸'}</span>
        <strong>{node.clauseNumber}</strong>
        <span>{node.title}</span>
        {node.mandatory && <Badge tone="neutral">mandatory</Badge>}
        {node.questions.length > 0 && <Badge tone="primary">{node.questions.length} q</Badge>}
      </button>
      {expanded && (
        <div style={{ marginLeft: 24, paddingBottom: 8 }}>
          {node.description && <p style={{ color: 'var(--color-text-muted)' }}>{node.description}</p>}
          {node.guidanceNotes && (
            <p style={{ color: 'var(--color-text-faint)', fontSize: 'var(--text-xs)' }}>
              Guidance: {node.guidanceNotes}
            </p>
          )}
          {node.evidenceRequirements && (
            <p style={{ color: 'var(--color-text-faint)', fontSize: 'var(--text-xs)' }}>
              Evidence: {node.evidenceRequirements}
            </p>
          )}
          {node.corpusKeys.length > 0 && (
            <p>
              {node.corpusKeys.map((k) => (
                <Badge key={k}>{k}</Badge>
              ))}
            </p>
          )}
          {node.questions.map((q) => (
            <div
              key={q.id}
              style={{
                borderLeft: '2px solid var(--color-border-strong)',
                paddingLeft: 12,
                marginBottom: 8,
              }}
            >
              <p style={{ marginBottom: 2 }}>{q.text}</p>
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', marginBottom: 0 }}>
                <Badge tone="info">{q.responseType}</Badge>{' '}
                {q.requiredEvidence && <Badge tone="warning">evidence required</Badge>} {q.helpText}
              </p>
            </div>
          ))}
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {node.children.map((child) => (
              <ClauseNode key={child.id} node={child} search={search} />
            ))}
          </ul>
        </div>
      )}
    </li>
  );
}

export function StandardDetailPage() {
  const { versionId = '' } = useParams();
  const { org } = useOrg();
  const [search, setSearch] = useState('');
  const [diffAgainst, setDiffAgainst] = useState('');
  const [showDiff, setShowDiff] = useState(false);

  const detail = useQuery({
    queryKey: ['version-detail', versionId],
    queryFn: () => apiFetch<StandardVersionDetail>(`/standard-versions/${versionId}`),
  });
  const versions = useQuery({
    queryKey: ['standard-versions', detail.data?.standard.id],
    queryFn: () => apiFetch<StandardVersion[]>(`/standards/${detail.data!.standard.id}/versions`),
    enabled: !!detail.data,
  });
  const diff = useQuery({
    queryKey: ['version-diff', diffAgainst, versionId],
    queryFn: () => apiFetch<VersionDiff>(`/standard-versions/${diffAgainst}/diff/${versionId}`),
    enabled: showDiff && !!diffAgainst,
  });

  const counts = useMemo(() => {
    if (!detail.data) return { clauses: 0, questions: 0 };
    let clauses = 0;
    let questions = 0;
    const walk = (nodes: ClauseTreeNode[]) => {
      for (const n of nodes) {
        clauses++;
        questions += n.questions.length;
        walk(n.children);
      }
    };
    walk(detail.data.clauses);
    return { clauses, questions };
  }, [detail.data]);

  if (detail.isLoading) return <LoadingPane label="Loading standard" />;
  if (!detail.data) return null;
  const { standard, version, clauses } = detail.data;
  const otherVersions = (versions.data ?? []).filter((v) => v.id !== versionId);

  return (
    <div>
      <p>
        <Link to="/standards">← Standards catalogue</Link>
      </p>
      <PageHeader
        title={`${standard.name}`}
        subtitle={
          <>
            <Badge tone="primary">{version.label}</Badge>{' '}
            <Badge tone={version.status === 'active' ? 'success' : 'neutral'}>{version.status}</Badge> ·{' '}
            {counts.clauses} clauses · {counts.questions} questions
            {version.publicationDate && <> · published {formatDate(version.publicationDate)}</>}
          </>
        }
        actions={
          <>
            {/* Only a standard the switch is on for has a Statement of
                Applicability, and its endpoints are member-only: an assessor
                would get a 403. */}
            {standard.supportsSoa && org.role !== 'assessor' && (
              <Link to={`/standards/versions/${versionId}/soa`}>
                <Button variant="secondary">Statement of Applicability</Button>
              </Link>
            )}
            <a href={apiUrl(`/standard-versions/${versionId}/export`)}>
              <Button variant="secondary">Download bundle</Button>
            </a>
          </>
        }
      />

      {(version.changeSummary || version.migrationGuidance) && (
        <Card title="About this version">
          {version.changeSummary && <p>{version.changeSummary}</p>}
          {version.migrationGuidance && (
            <p style={{ color: 'var(--color-text-muted)' }}>
              <strong>Migration guidance:</strong> {version.migrationGuidance}
            </p>
          )}
        </Card>
      )}

      {otherVersions.length > 0 && (
        <Card title="Compare with another version">
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <Select
              aria-label="Version to compare from"
              value={diffAgainst}
              onChange={(e) => {
                setDiffAgainst(e.target.value);
                setShowDiff(false);
              }}
              style={{ width: 'auto', minWidth: 200 }}
            >
              <option value="">Choose a version…</option>
              {otherVersions.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.label}
                </option>
              ))}
            </Select>
            <span style={{ color: 'var(--color-text-faint)' }}>→ {version.label}</span>
            <Button size="sm" variant="secondary" disabled={!diffAgainst} onClick={() => setShowDiff(true)}>
              Compare
            </Button>
          </div>

          {showDiff && diff.data && (
            <div style={{ marginTop: 16 }}>
              <p>
                {(['new', 'changed', 'removed', 'unchanged'] as const).map((k) => (
                  <Badge key={k} tone={CHANGE_TONES[k]}>
                    {diff.data.summary[k]} {k}
                  </Badge>
                ))}
              </p>
              <Table
                head={
                  <>
                    <th>Change</th>
                    <th>Clause</th>
                    <th>Title</th>
                    <th>Questions</th>
                  </>
                }
              >
                {diff.data.entries
                  .filter((e) => e.changeType !== 'unchanged')
                  .map((e) => (
                    <tr key={`${e.clauseNumber}-${e.changeType}`}>
                      <td>
                        <Badge tone={CHANGE_TONES[e.changeType]}>{e.changeType}</Badge>
                      </td>
                      <td>{e.clauseNumber}</td>
                      <td>{e.title}</td>
                      <td>
                        {e.questionCountFrom} → {e.questionCountTo}
                      </td>
                    </tr>
                  ))}
              </Table>
            </div>
          )}
        </Card>
      )}

      <Card title="Clauses and questions">
        <Input
          aria-label="Search clauses and questions"
          placeholder="Search clauses and questions…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 320, marginBottom: 12 }}
        />
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {clauses.map((node) => (
            <ClauseNode key={node.id} node={node} search={search} />
          ))}
        </ul>
      </Card>
    </div>
  );
}
